<template>
	<div class="user-management">
		<!-- Заголовок -->
		<div class="page-header">
			<h1 class="page-title">Управление пользователями</h1>
			<button @click="openCreateModal" class="btn-primary">
				<span class="icon">+</span>
				Добавить пользователя
			</button>
		</div>

		<!-- Фильтры и поиск -->
		<div class="filters-section">
			<div class="search-box">
				<input
					v-model="searchQuery"
					type="text"
					placeholder="Поиск по имени или email..."
					class="search-input"
					@input="handleSearch"
				/>
			</div>

			<div class="filters">
				<select v-model="roleFilter" @change="loadUsers" class="filter-select">
					<option value="">Все роли</option>
					<option value="admin">Администраторы</option>
					<option value="teacher">Преподаватели</option>
					<option value="student">Студенты</option>
				</select>

				<select
					v-model="statusFilter"
					@change="loadUsers"
					class="filter-select"
				>
					<option value="">Все статусы</option>
					<option value="active">Активные</option>
					<option value="blocked">Заблокированные</option>
				</select>
			</div>
		</div>

		<!-- Таблица пользователей -->
		<div class="table-container">
			<div v-if="loading" class="loading">Загрузка...</div>
			<table v-else class="users-table">
				<thead>
					<tr>
						<th>ID</th>
						<th>Имя</th>
						<th>Email</th>
						<th>Роль</th>
						<th>Статус</th>
						<th>Дата регистрации</th>
						<th>Последний вход</th>
						<th>Действия</th>
					</tr>
				</thead>
				<tbody>
					<tr v-if="users.length === 0">
						<td colspan="8" class="empty-cell">Пользователи не найдены</td>
					</tr>
					<tr
						v-else
						v-for="user in users"
						:key="user.id"
						class="user-row"
						:class="{ 'user-row--blocked': !user.is_active }"
					>
						<td>{{ user.id }}</td>
						<td>
							<div class="user-info">
								<div class="user-avatar">{{ getInitials(user.full_name) }}</div>
								<span>{{ user.full_name }}</span>
							</div>
						</td>
						<td>{{ user.email }}</td>
						<td>
							<span class="role-badge" :class="`role-${user.roles[0]}`">
								{{ getRoleLabel(user.roles[0]) }}
							</span>
						</td>
						<td>
							<span
								class="status-badge"
								:class="user.is_active ? 'active' : 'blocked'"
							>
								{{ user.is_active ? 'Активен' : 'Заблокирован' }}
							</span>
						</td>
						<td>{{ formatDate(user.created_at) }}</td>
						<td>
							{{
								user.last_activity_at ? formatDate(user.last_activity_at) : 'Никогда'
							}}
						</td>
						<td>
							<div class="actions">
								<button
									@click="editUser(user)"
									class="btn-action btn-edit"
									title="Редактировать"
								>
									✏️
								</button>
								<button
									@click="toggleStatus(user)"
									class="btn-action btn-toggle"
									:title="user.is_active ? 'Заблокировать' : 'Разблокировать'"
								>
									{{ user.is_active ? '🔒' : '🔓' }}
								</button>
								<button
									@click="confirmDelete(user)"
									class="btn-action btn-delete"
									title="Удалить"
								>
									🗑️
								</button>
							</div>
						</td>
					</tr>
				</tbody>
			</table>
		</div>

		<!-- Пагинация -->
		<div v-if="pagination.totalPages > 1" class="pagination">
			<button
				@click="changePage(pagination.page - 1)"
				:disabled="pagination.page === 1"
				class="btn-page"
			>
				← Назад
			</button>
			<span class="page-info"
				>Страница {{ pagination.page }} из {{ pagination.totalPages }}</span
			>
			<button
				@click="changePage(pagination.page + 1)"
				:disabled="pagination.page === pagination.totalPages"
				class="btn-page"
			>
				Вперед →
			</button>
		</div>

		<!-- Модальное окно создания/редактирования -->
		<div v-if="showModal" class="modal-overlay" @click.self="closeModal">
			<div class="modal">
				<div class="modal-header">
					<h2>
						{{
							isEditing ? 'Редактировать пользователя' : 'Создать пользователя'
						}}
					</h2>
					<button @click="closeModal" class="btn-close">×</button>
				</div>
				<div class="modal-body">
					<div class="form-group">
						<label>Полное имя *</label>
						<input
							v-model="formData.full_name"
							type="text"
							class="form-input"
							required
						/>
					</div>
					<div class="form-group">
						<label>Email *</label>
						<input
							v-model="formData.email"
							type="email"
							class="form-input"
							required
						/>
					</div>
					<div v-if="!isEditing" class="form-group">
						<label>Пароль *</label>
						<input
							v-model="formData.password"
							type="password"
							class="form-input"
							required
						/>
					</div>
					<div class="form-group">
						<label>Роль *</label>
						<select v-model="formData.role" class="form-input" required>
							<option value="student">Студент</option>
							<option value="teacher">Преподаватель</option>
							<option value="admin">Администратор</option>
						</select>
					</div>
				</div>
				<div class="modal-footer">
					<button @click="closeModal" class="btn-secondary">Отмена</button>
					<button @click="saveUser" class="btn-primary">
						{{ isEditing ? 'Сохранить' : 'Создать' }}
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import {
	getAllUsers,
	createUser,
	updateUser,
	toggleUserStatus,
	deleteUser,
} from '@/services/users'

export default {
	name: 'UserManagement',
	data() {
		return {
			users: [],
			loading: false,
			searchQuery: '',
			roleFilter: '',
			statusFilter: '',
			pagination: { page: 1, limit: 20, total: 0, totalPages: 0 },
			showModal: false,
			isEditing: false,
			formData: { full_name: '', email: '', password: '', role: 'student' },
			editingUserId: null,
			searchTimeout: null,
		}
	},
	mounted() {
		this.loadUsers()
	},
	methods: {
		async loadUsers() {
			this.loading = true
			try {
				const params = {
					page: this.pagination.page,
					limit: this.pagination.limit,
				}
				if (this.roleFilter) params.role = this.roleFilter
				if (this.statusFilter) params.status = this.statusFilter
				if (this.searchQuery) params.search = this.searchQuery

				const response = await getAllUsers(params)
				this.users = response.data.users
				this.pagination = response.data.pagination
			} catch (error) {
				console.error('Ошибка загрузки пользователей:', error)
				alert('Ошибка загрузки пользователей')
			} finally {
				this.loading = false
			}
		},
		handleSearch() {
			clearTimeout(this.searchTimeout)
			this.searchTimeout = setTimeout(() => {
				this.pagination.page = 1
				this.loadUsers()
			}, 500)
		},
		changePage(page) {
			this.pagination.page = page
			this.loadUsers()
		},
		openCreateModal() {
			this.isEditing = false
			this.formData = {
				full_name: '',
				email: '',
				password: '',
				role: 'student',
			}
			this.showModal = true
		},
		editUser(user) {
			this.isEditing = true
			this.editingUserId = user.id
			this.formData = {
				full_name: user.full_name,
				email: user.email,
				role: user.roles[0],
			}
			this.showModal = true
		},
		closeModal() {
			this.showModal = false
			this.isEditing = false
			this.editingUserId = null
		},
		async saveUser() {
			try {
				if (this.isEditing) {
					await updateUser(this.editingUserId, this.formData)
					alert('Пользователь успешно обновлен')
				} else {
					await createUser(this.formData)
					alert('Пользователь успешно создан')
				}
				this.closeModal()
				this.loadUsers()
			} catch (error) {
				console.error('Ошибка сохранения:', error)
				alert(error.response?.data?.message || 'Ошибка сохранения пользователя')
			}
		},
		async toggleStatus(user) {
			try {
				await toggleUserStatus(user.id)
				alert(
					user.is_active
						? 'Пользователь заблокирован'
						: 'Пользователь разблокирован'
				)
				this.loadUsers()
			} catch (error) {
				console.error('Ошибка изменения статуса:', error)
				alert('Ошибка изменения статуса пользователя')
			}
		},
		async confirmDelete(user) {
			if (
				!confirm(
					`Вы уверены, что хотите удалить пользователя "${user.full_name}"?`
				)
			)
				return
			try {
				await deleteUser(user.id)
				alert('Пользователь успешно удален')
				this.loadUsers()
			} catch (error) {
				console.error('Ошибка удаления:', error)
				alert(error.response?.data?.message || 'Ошибка удаления пользователя')
			}
		},
		getInitials(fullName) {
			return fullName
				.split(' ')
				.map(n => n[0])
				.join('')
				.toUpperCase()
				.slice(0, 2)
		},
		getRoleLabel(role) {
			const labels = {
				student: 'Студент',
				teacher: 'Преподаватель',
				admin: 'Администратор',
			}
			return labels[role] || role
		},
		formatDate(date) {
			if (!date) return ''
			return new Date(date).toLocaleDateString('ru-RU', {
				year: 'numeric',
				month: '2-digit',
				day: '2-digit',
			})
		},
	},
}
</script>

<style scoped>
.user-management {
	padding: 30px;
	background-color: #f8f9fa;
	min-height: 100vh;
	font-family: 'Poppins', Helvetica;
}

.page-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 30px;
}

.page-title {
	color: #252641;
	font-size: 32px;
	font-weight: 700;
	margin: 0;
}

.btn-primary {
	display: flex;
	align-items: center;
	gap: 8px;
	background-color: #0095ec;
	color: #ffffff;
	border: none;
	border-radius: 8px;
	padding: 12px 24px;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.3s ease;
}

.btn-primary:hover {
	background-color: #0077be;
	transform: translateY(-2px);
	box-shadow: 0 4px 12px rgba(0, 149, 236, 0.3);
}

.icon {
	font-size: 20px;
}

.filters-section {
	display: flex;
	gap: 16px;
	margin-bottom: 24px;
}

.search-box {
	flex: 1;
}

.search-input {
	width: 100%;
	padding: 12px 16px;
	border: 2px solid #e8e8e8;
	border-radius: 8px;
	font-size: 14px;
	transition: all 0.2s ease;
}

.search-input:focus {
	outline: none;
	border-color: #0095ec;
}

.filters {
	display: flex;
	gap: 12px;
}

.filter-select {
	padding: 12px 16px;
	border: 2px solid #e8e8e8;
	border-radius: 8px;
	font-size: 14px;
	cursor: pointer;
	background-color: #ffffff;
}

.table-container {
	background-color: #ffffff;
	border-radius: 12px;
	padding: 24px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.loading {
	text-align: center;
	padding: 60px;
	color: #636363;
	font-size: 16px;
}

.users-table {
	width: 100%;
	border-collapse: collapse;
}

.users-table th {
	text-align: left;
	padding: 12px;
	color: #636363;
	font-size: 13px;
	font-weight: 500;
	border-bottom: 2px solid #e8e8e8;
}

.users-table td {
	padding: 16px 12px;
	color: #252641;
	font-size: 14px;
	border-bottom: 1px solid #e8e8e8;
}

.user-row--blocked {
	background-color: #ffe5e5;
}

.user-row--blocked td {
	color: #8a1f1f;
	text-decoration: line-through;
}

.user-info {
	display: flex;
	align-items: center;
	gap: 12px;
}

.user-avatar {
	width: 40px;
	height: 40px;
	border-radius: 50%;
	background-color: #0095ec;
	color: #ffffff;
	display: flex;
	align-items: center;
	justify-content: center;
	font-weight: 600;
}

.role-badge {
	padding: 4px 12px;
	border-radius: 12px;
	font-size: 12px;
	font-weight: 500;
}

.role-student {
	background-color: #d1ecf1;
	color: #0c5460;
}

.role-teacher {
	background-color: #d4edda;
	color: #155724;
}

.role-admin {
	background-color: #f8d7da;
	color: #721c24;
}

.status-badge {
	padding: 4px 12px;
	border-radius: 12px;
	font-size: 12px;
	font-weight: 500;
}

.status-badge.active {
	background-color: #d4edda;
	color: #155724;
}

.status-badge.blocked {
	background-color: #f8d7da;
	color: #721c24;
}

.actions {
	display: flex;
	gap: 8px;
}

.btn-action {
	width: 36px;
	height: 36px;
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: #f5f5f5;
	border: none;
	border-radius: 8px;
	font-size: 16px;
	cursor: pointer;
	transition: all 0.2s ease;
}

.btn-action:hover {
	background-color: #e8e8e8;
}

.btn-delete:hover {
	background-color: #f8d7da;
}

.pagination {
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 20px;
	margin-top: 24px;
}

.btn-page {
	padding: 10px 20px;
	background-color: #ffffff;
	border: 2px solid #e8e8e8;
	border-radius: 8px;
	font-size: 14px;
	cursor: pointer;
	transition: all 0.2s ease;
}

.btn-page:hover:not(:disabled) {
	background-color: #0095ec;
	color: #ffffff;
	border-color: #0095ec;
}

.btn-page:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

.page-info {
	color: #636363;
	font-size: 14px;
}

.modal-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.5);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1000;
}

.modal {
	background-color: #ffffff;
	border-radius: 12px;
	padding: 24px;
	max-width: 500px;
	width: 90%;
	max-height: 90vh;
	overflow-y: auto;
}

.modal-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 24px;
	padding-bottom: 16px;
	border-bottom: 2px solid #e8e8e8;
}

.modal-header h2 {
	color: #252641;
	font-size: 22px;
	font-weight: 600;
	margin: 0;
}

.btn-close {
	width: 32px;
	height: 32px;
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: transparent;
	border: none;
	border-radius: 50%;
	color: #636363;
	font-size: 24px;
	cursor: pointer;
	transition: all 0.2s ease;
}

.btn-close:hover {
	background-color: #f5f5f5;
	color: #dc3545;
}

.modal-body {
	display: flex;
	flex-direction: column;
	gap: 20px;
}

.form-group {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.form-group label {
	color: #252641;
	font-size: 14px;
	font-weight: 500;
}

.form-input {
	padding: 12px 16px;
	border: 2px solid #e8e8e8;
	border-radius: 8px;
	font-size: 14px;
	color: #252641;
	transition: all 0.2s ease;
}

.form-input:focus {
	outline: none;
	border-color: #0095ec;
	box-shadow: 0 0 0 3px rgba(0, 149, 236, 0.1);
}

.modal-footer {
	display: flex;
	gap: 12px;
	justify-content: flex-end;
	margin-top: 24px;
	padding-top: 20px;
	border-top: 2px solid #e8e8e8;
}

.btn-secondary {
	padding: 12px 24px;
	background-color: #f5f5f5;
	color: #636363;
	border: none;
	border-radius: 8px;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s ease;
}

.btn-secondary:hover {
	background-color: #e8e8e8;
}

.empty-cell {
	text-align: center;
	padding: 60px;
	color: #636363;
	font-size: 16px;
}
</style>
